import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { DriverApi, OnboardingApplicationRecord } from "../driver-api.ts";
import { boardChip, boardSub, boardTitle, stepChecks } from "../onboarding-board.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Onboarding board` — what the platform is doing about this driver's application.
 *
 * `onboarding-board.ts` beside this file holds every reading and the reasons for each; this is the
 * frame around it. Two things about the frame itself are decisions rather than plumbing.
 *
 * **The application is named in the address, because nothing lists one.** DRV-02 publishes a
 * by-identifier read and no listing at any tenancy bound, so there is no operation that answers
 * "which application is mine" — the identifier reaches this screen the way a cart reaches the rider
 * app's checkout, from whatever handed the driver the link. A screen with no identifier says so
 * rather than guessing, and says which operation would have to exist.
 *
 * **The `Retake vehicle photos` button is drawn and refused.** The design puts it here and the
 * house rules forbid removing it. Retaking a photograph is FIL-02's upload followed by DRV-02's
 * verify, and the verify is `driver:onboarding:verify` — a **reviewer's** scope. A driver re-submits
 * by uploading, and nothing published connects an upload to a step. So the control explains that
 * rather than appearing to work.
 */

export interface OnboardingBoardProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** The application this screen is about. `undefined` when the address names none. */
  readonly applicationId: string | undefined;
}

const RETAKE_UNAVAILABLE =
  "Retaking a photo is FIL-02's upload and then DRV-02's verify, and the verify is driver:onboarding:verify — a reviewer's scope. Nothing published connects an upload to an onboarding step.";

export function OnboardingBoard({ api, tenantId, applicationId }: OnboardingBoardProps): ReactNode {
  const [application, setApplication] = useState<OnboardingApplicationRecord | undefined>(
    undefined,
  );
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  /**
   * The application in the address, or this driver's own newest if the address names none.
   *
   * The listing is bounded to the caller by the self scope, so `applications[0]` is this
   * driver's most recent and nobody else's. Ordered newest first on a creation instant no
   * transition rewrites, which is what makes "the first one" a stable answer rather than one
   * that changes while a reviewer verifies a step.
   *
   * An empty page is not an error. A driver who has not applied has no application, and the
   * screen says that rather than reporting a failure.
   */
  const load = useCallback(() => {
    setState({ kind: "loading" });
    const asked =
      applicationId === undefined
        ? api.onboardingApplications({ tenantId }).then((applications) => applications[0])
        : api.onboardingApplication({ tenantId, applicationId });
    asked
      .then((answer) => {
        setApplication(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, ["driver:onboarding:self"])));
  }, [api, tenantId, applicationId]);

  useEffect(load, [load]);

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="onboarding-board"
        state={state}
        onRetry={load}
        purpose="Checking where your application has got to."
      />
    );
  }

  if (application === undefined) {
    /*
     * A read that worked and found nothing, which is not a failure.
     *
     * Drawn as its own state rather than as an error, because a driver who has not applied has
     * no application and telling them something went wrong would send them looking for a fault.
     */
    return (
      <StateScreen
        screen="onboarding-board"
        chip={{ class: "idle", label: "no application" }}
        title="You have not applied yet"
        sub="Nothing has been submitted under your account. When an application exists, this is where it will be."
      />
    );
  }

  const title = boardTitle(application);
  const sub = boardSub(application);
  const flagged = !title.designed || !sub.designed;

  return (
    <PhoneShell
      screen="onboarding-board"
      /* `art:'idle'` — the shared untinted disc, as `Vehicle` draws it for the same flag. */
      art={<HeroDisc glyph="power_settings_new" caption="Offline" />}
      /*
       * `on:0`, the design's own flag. A driver whose application is still being reviewed has no
       * shift to be on, and presence is DRV-05's — not read here, and not composed to answer a pill.
       */
      online={false}
      earningsLabel={null}
      chip={boardChip(application)}
      title={title.text}
      sub={sub.text}
      checks={stepChecks(application)}
      actions={[
        {
          label: "Retake vehicle photos",
          glyph: "photo_camera",
          primary: true,
          unavailable: RETAKE_UNAVAILABLE,
        },
      ]}
      {...(flagged ? { flag: "wording not in the design" } : {})}
    />
  );
}
